import React from "react";
import { Inbox } from "lucide-react";

export const MINT = "#10b981";
export const PURPLE = "#a855f7";

export const formatPrice = (value) =>
  typeof value === "number" && !Number.isNaN(value)
    ? `$${value.toFixed(2)}`
    : "-";

export const StatusBadge = ({ status }) => {
  const normalizedStatus = (status || "").toLowerCase();
  const map = {
    pending: { color: "#f59e0b", label: "Pending" },
    current: { color: PURPLE, label: "Current" },
    progress: { color: PURPLE, label: "In Progress" },
    revision: { color: "#f59e0b", label: "Revision" },
    submitted: { color: PURPLE, label: "Submitted" },
    delivered: { color: PURPLE, label: "Delivered" },
    approved: { color: MINT, label: "Approved" },
    completed: { color: MINT, label: "Completed" },
    rejected: { color: "#ef4444", label: "Rejected" },
  };
  const config = map[normalizedStatus] || {
    color: "#94a3b8",
    label: status || "-",
  };

  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider"
      style={{
        color: config.color,
        background: `${config.color}1f`,
        borderColor: `${config.color}40`,
      }}
    >
      <span
        className="h-1.5 w-1.5 rounded-full"
        style={{
          background: config.color,
          boxShadow: `0 0 8px ${config.color}`,
        }}
      />
      {config.label}
    </span>
  );
};

export const FilterTabs = ({ tabs, value, onChange }) => (
  <div className="rounded-2xl border border-white/10 bg-black/40 p-1 backdrop-blur-xl">
    <div className="flex flex-wrap gap-1">
      {tabs.map((tab) => {
        const active = value === tab.id;

        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => onChange(tab.id)}
            className={`min-w-[80px] flex-1 rounded-xl px-3 py-2 text-xs font-semibold uppercase tracking-wider transition ${
              active ? "text-white" : "text-white/50 hover:text-white/80"
            }`}
            style={
              active
                ? {
                    background: "rgba(255,255,255,0.06)",
                    boxShadow: `inset 0 0 0 1px ${MINT}40, 0 8px 24px -12px ${MINT}`,
                  }
                : undefined
            }
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  </div>
);

export const EmptyOrders = ({
  message = "No orders available",
  hint = "When you have orders, they'll appear here.",
}) => (
  <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-10 text-center backdrop-blur-xl">
    <div
      className="pointer-events-none absolute -top-16 left-1/2 h-40 w-40 -translate-x-1/2 rounded-full opacity-20 blur-3xl"
      style={{ background: PURPLE }}
    />
    <div className="relative mx-auto grid h-14 w-14 place-items-center rounded-2xl border border-white/10 bg-white/5">
      <Inbox size={22} className="text-white/60" />
    </div>
    <p className="relative mt-4 text-sm font-semibold text-white">{message}</p>
    <p className="relative mt-1 text-xs text-white/40">{hint}</p>
  </div>
);

export const MetricCard = ({ label, value, accent = "mint" }) => {
  const color = accent === "mint" ? MINT : PURPLE;

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] p-4"
      style={{ boxShadow: `0 8px 30px -20px ${color}88` }}
    >
      <p className="text-[10px] font-semibold uppercase tracking-wider text-white/40">
        {label}
      </p>
      <p className="mt-1.5 text-2xl font-bold text-white">{value}</p>
      <div
        className="pointer-events-none absolute -bottom-8 -right-6 h-20 w-20 rounded-full opacity-20 blur-2xl"
        style={{ background: color }}
      />
    </div>
  );
};

export const OverlayLoader = ({ message = "Processing..." }) => (
  <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-3 bg-black/70 backdrop-blur-sm">
    <div className="relative h-12 w-12">
      <div
        className="absolute inset-0 animate-spin rounded-full border-2 border-transparent"
        style={{ borderTopColor: MINT, borderRightColor: PURPLE }}
      />
    </div>
    <p className="text-sm text-white/70">{message}</p>
  </div>
);
